import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { sessionGet, sessionSet, purgeLegacyLocal } from "@/lib/sessionStore";

const STORAGE_KEY = "nc_cookie_consent_v1";

const COPY = {
  en: {
    title: "Cookies & Privacy",
    body: "We use strictly necessary cookies to operate this site, and — only with your consent — analytics or marketing cookies. You can change your choices at any time.",
    necessary: "Strictly necessary",
    analytics: "Analytics",
    marketing: "Marketing",
    acceptAll: "Accept all",
    rejectAll: "Reject non-essential",
    save: "Save preferences",
    learnMore: "Cookie Policy",
  },
  de: {
    title: "Cookies & Datenschutz",
    body: "Wir verwenden unbedingt erforderliche Cookies für den Betrieb dieser Website. Analyse- und Marketing-Cookies nur mit deiner Einwilligung. Du kannst deine Auswahl jederzeit ändern.",
    necessary: "Unbedingt erforderlich",
    analytics: "Analyse",
    marketing: "Marketing",
    acceptAll: "Alle akzeptieren",
    rejectAll: "Nur notwendige",
    save: "Auswahl speichern",
    learnMore: "Cookie-Richtlinie",
  },
  pt: {
    title: "Cookies & Privacidade",
    body: "Utilizamos cookies estritamente necessários para o funcionamento do site. Cookies de análise ou marketing apenas com o teu consentimento. Podes alterar as tuas escolhas a qualquer momento.",
    necessary: "Estritamente necessários",
    analytics: "Análise",
    marketing: "Marketing",
    acceptAll: "Aceitar tudo",
    rejectAll: "Apenas essenciais",
    save: "Guardar preferências",
    learnMore: "Política de Cookies",
  },
};

const CookieBanner = () => {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;
  const [open, setOpen] = useState(() => !sessionGet(STORAGE_KEY));
  const [details, setDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => purgeLegacyLocal(STORAGE_KEY), []);

  // Session-scoped: the choice is forgotten when the browser closes.
  const persist = (prefs) => {
    sessionSet(STORAGE_KEY, JSON.stringify({ ...prefs, ts: Date.now(), v: 1 }));
    setOpen(false);
  };

  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => persist({ necessary: true, analytics, marketing });

  if (!open) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="max-w-[1400px] mx-auto bg-[#0F1B2D] text-white shadow-2xl border border-[#C4A46A]/30">
        <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-7">
            <div className="overline text-[#C4A46A]">{c.title}</div>
            <p className="mt-3 text-white/80 leading-relaxed text-[15px]">{c.body}</p>
            <a
              href={`/${lang}/legal/cookies`}
              className="mt-3 inline-block text-[#C4A46A] hover:text-white text-[12px] tracking-[0.2em] uppercase font-bold"
            >
              {c.learnMore} →
            </a>

            {details && (
              <div className="mt-6 space-y-3 text-[14px]">
                <div className="flex items-center justify-between border-t border-white/10 pt-3">
                  <span className="text-white/80">{c.necessary}</span>
                  <span className="text-[#C4A46A] text-[11px] tracking-[0.2em] uppercase font-bold">Always on</span>
                </div>
                <label className="flex items-center justify-between border-t border-white/10 pt-3 cursor-pointer">
                  <span className="text-white/80">{c.analytics}</span>
                  <input
                    type="checkbox"
                    data-testid="cookie-analytics"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="h-4 w-4 accent-[#C4A46A]"
                  />
                </label>
                <label className="flex items-center justify-between border-t border-white/10 pt-3 cursor-pointer">
                  <span className="text-white/80">{c.marketing}</span>
                  <input
                    type="checkbox"
                    data-testid="cookie-marketing"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    className="h-4 w-4 accent-[#C4A46A]"
                  />
                </label>
              </div>
            )}
          </div>
          <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-stretch">
            <button
              onClick={acceptAll}
              data-testid="cookie-accept-all"
              className="px-5 py-3 bg-[#C4A46A] text-[#0F1B2D] text-[11px] tracking-[0.24em] uppercase font-bold hover:bg-[#A38755] transition-colors"
            >
              {c.acceptAll}
            </button>
            {details ? (
              <button
                onClick={saveCustom}
                data-testid="cookie-save"
                className="px-5 py-3 border border-[#C4A46A] text-[#C4A46A] text-[11px] tracking-[0.24em] uppercase font-bold hover:bg-[#C4A46A]/10"
              >
                {c.save}
              </button>
            ) : (
              <button
                onClick={() => setDetails(true)}
                data-testid="cookie-customize"
                className="px-5 py-3 border border-white/20 text-white/85 text-[11px] tracking-[0.24em] uppercase font-bold hover:bg-white/5"
              >
                Customize
              </button>
            )}
            <button
              onClick={rejectAll}
              data-testid="cookie-reject"
              className="px-5 py-3 text-white/60 text-[11px] tracking-[0.24em] uppercase font-bold hover:text-white"
            >
              {c.rejectAll}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
